import React from 'react';
import { ArrowRight, MapPin, Shield, Clock, CheckCircle, Truck, Lock } from 'lucide-react';
import SEO from '../components/SEO';

const StorageVanAlstyne = () => {
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'SelfStorage',
    name: 'VD Storage Bells – Serving Van Alstyne, TX',
    url: 'https://vdstoragebells.com/storage-van-alstyne-tx',
    telephone: '(903) 527-3992',
    address: {
      '@type': 'PostalAddress',
      streetAddress: '805 S. 3rd Street',
      addressLocality: 'Bells',
      addressRegion: 'TX',
      postalCode: '75414',
      addressCountry: 'US'
    },
    description: 'Self storage near Van Alstyne, TX. VD Storage Bells offers drive-up units with gated access — just a short drive from Van Alstyne on Hwy 82.',
    priceRange: '$'
  };

  const benefits = [
    'Short drive from Van Alstyne via Hwy 82',
    'Drive-up units — no stairs, no elevators',
    'Electronic gate with secure 24/7 access',
    'Month-to-month rentals — no long-term commitment',
    'Rent online in minutes from your phone',
    'Affordable rates with no hidden fees',
    'Well-lit facility with video surveillance',
    'Friendly local management'
  ];

  const unitSizes = [
    { size: '5x10', ideal: 'Boxes, seasonal items, small furniture', use: 'Dorm room / small apartment' },
    { size: '10x10', ideal: '1–2 bedroom apartment contents', use: 'Average bedroom' },
    { size: '10x15', ideal: '2–3 bedroom home contents', use: 'Large master bedroom' },
    { size: '10x20', ideal: 'Full house or contractor equipment', use: 'Single-car garage' }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title="Storage Units near Van Alstyne, TX | VD Storage Bells"
        description="Self storage near Van Alstyne, TX at VD Storage Bells. Drive-up units, gated access, affordable monthly rates. Just minutes away on Hwy 82. Rent online today."
        canonical="/storage-van-alstyne-tx"
        structuredData={structuredData}
      />

      {/* Header */}
      <section className="bg-green-600 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4" data-testid="van-alstyne-title">
            Storage Units near Van Alstyne, TX
          </h1>
          <p className="text-xl text-green-100 max-w-3xl mx-auto">
            VD Storage Bells — your closest self storage option, just minutes from Van Alstyne on Hwy 82
          </p>
        </div>
      </section>

      {/* Intro */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xl text-gray-700 leading-relaxed mb-6">
            Need storage near Van Alstyne? VD Storage Bells is the closest full-service self storage facility to Van Alstyne, located just a few miles east on Hwy 82 in Bells, TX. We offer drive-up units in multiple sizes — ideal for households, small businesses, and contractors in the Van Alstyne area.
          </p>
          <p className="text-lg text-gray-700 leading-relaxed">
            Van Alstyne is growing fast, and storage space at home is harder to come by. Whether you're storing during a move, decluttering, or need space for business inventory, we've got you covered.
          </p>
        </div>
      </section>

      {/* Unit Sizes */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Available Unit Sizes
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              All units are drive-up accessible — pull right to your door
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {unitSizes.map((unit, index) => (
              <div key={index} className="bg-white p-6 rounded-xl shadow-md text-center">
                <div className="text-3xl font-bold text-green-600 mb-2">{unit.size}</div>
                <div className="text-sm font-medium text-gray-500 mb-3">{unit.use}</div>
                <p className="text-gray-700 text-sm">{unit.ideal}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why Van Alstyne Residents Choose Us
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-gray-50 p-8 rounded-xl text-center">
              <MapPin className="h-12 w-12 text-green-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Closest Facility</h3>
              <p className="text-gray-600">Minutes from Van Alstyne via Hwy 82 — less driving, more convenience.</p>
            </div>
            <div className="bg-gray-50 p-8 rounded-xl text-center">
              <Truck className="h-12 w-12 text-green-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Drive-Up Access</h3>
              <p className="text-gray-600">Pull your vehicle directly to your unit door. No carts, no elevators needed.</p>
            </div>
            <div className="bg-gray-50 p-8 rounded-xl text-center">
              <Shield className="h-12 w-12 text-green-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Secure Facility</h3>
              <p className="text-gray-600">Gated entry with electronic access and 24/7 video surveillance.</p>
            </div>
            <div className="bg-gray-50 p-8 rounded-xl text-center">
              <Lock className="h-12 w-12 text-green-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Rent Online</h3>
              <p className="text-gray-600">Reserve your unit from your phone — no paperwork, no office visit.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 bg-green-50 border-t border-b border-green-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">
            What You Get at VD Storage Bells
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {benefits.map((benefit, index) => (
              <div key={index} className="flex items-start space-x-3">
                <CheckCircle className="h-6 w-6 text-green-600 flex-shrink-0 mt-0.5" />
                <span className="text-lg text-gray-700">{benefit}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Directions */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Getting Here from Van Alstyne</h2>
          <p className="text-lg text-gray-700 leading-relaxed mb-4">
            From Van Alstyne, take Hwy 82 East toward Bells. VD Storage is located at 805 S. 3rd Street in Bells — about a 10-minute drive. The facility is easy to access and has wide aisles for trucks and trailers.
          </p>
          <p className="text-lg text-gray-700 leading-relaxed">
            We also serve residents from Sherman, Howe, and the surrounding Grayson County area.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gray-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">Ready to Rent Near Van Alstyne?</h2>
          <p className="text-xl text-gray-300 mb-8">
            Check unit availability and rent online today — no office visit required.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="https://vdstoragebells.mysms360.com/rent?utm_source=vdstoragebells.com&utm_content=vanalstyne"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-8 py-3 text-lg font-semibold text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors shadow-lg"
              data-testid="rent-now-button"
            >
              Rent Online Now
              <ArrowRight className="ml-2 h-5 w-5" />
            </a>
            <a
              href="/contact"
              className="inline-flex items-center px-8 py-3 text-lg font-semibold text-gray-900 bg-white rounded-lg hover:bg-gray-100 transition-colors shadow-lg"
              data-testid="contact-button"
            >
              Contact Us
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default StorageVanAlstyne;
